/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

/**
 * UploadContentService - Handles uploading content files
 * API: POST /api/v2.0/upload
 */
export default class UploadContentService extends BaseService {
  constructor() {
    super(constants.UPLOAD_CONTENT, 'uploadContentStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { file, userId, s3Path, fileSize, contentPreview } = payload;

    const formData = new window.FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    if (s3Path) {
      formData.append('s3Path', s3Path);
    }

    this.fileSize = fileSize;
    this.contentPreview = contentPreview;

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackbv2').post('/upload', formData, { headers: enhancedHeaders });
    }
    return this.getClient('aiadminv2').post('/upload', formData, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      const uploadData = {
        ...data.data,
        fileSize: this.fileSize,
        contentPreview: this.contentPreview
      };

      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'success',
        langMessage: 'contentUpload.SUCCESS'
      });
      Framework.getStoreRegistry().getStore('contentUploadModel').setUploadSuccess(uploadData);
      Framework.getEventManager().publish(constants.UPLOAD_CONTENT_SUCCESS, { data: uploadData });
    } else {
      const serverMessage = data?.error || data?.message;
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          ...(serverMessage ? { message: serverMessage } : { langMessage: 'contentUpload.ERROR' })
        });
      Framework.getStoreRegistry()
        .getStore('contentUploadModel')
        .setUploadError(serverMessage || 'contentUpload.ERROR');
      Framework.getEventManager().publish(constants.UPLOAD_CONTENT_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
        Framework.getEventManager().publish(constants.UPLOAD_CONTENT_ERROR);
        return;
      }
      const serverMessage = data?.error || data?.message;
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          ...(serverMessage ? { message: serverMessage } : { langMessage: 'contentUpload.FAILED' })
        });
      Framework.getStoreRegistry()
        .getStore('contentUploadModel')
        .setUploadError(serverMessage || 'contentUpload.FAILED');
      Framework.getEventManager().publish(constants.UPLOAD_CONTENT_ERROR);
      return;
    }

    const langMessage =
      error.message === `timeout of ${constants.BERLIN_API_TIMEOUT}ms exceeded`
        ? 'contentUpload.TIMEOUT'
        : 'contentUpload.FAILED';

    Framework.getStoreRegistry().getStore('notificationState').set({
      open: true,
      type: 'error',
      langMessage
    });
    Framework.getStoreRegistry().getStore('contentUploadModel').setUploadError(langMessage);
    Framework.getEventManager().publish(constants.UPLOAD_CONTENT_ERROR);
  }
}
